<template>
  <div
    ref="root"
    class="step-line"
    :class="{ 'no-transition': noTransition, 'has-description': hasDescription }"
    :style="{ '--enter': ENTER + 'ms', '--move': MOVE + 'ms' }"
  >
    <div
      v-for="line in lines"
      :key="line.key"
      class="step-line__line"
      :class="{ 'is-done': line.done }"
      :style="{ transform: `translateX(${line.left}px)`, width: line.width + 'px' }"
    ></div>
    <div
      v-for="item in views"
      :key="item.key"
      class="step"
      :class="[
        'is-' + item.status,
        {
          'is-shown': item.shown,
          'is-label-shown': item.labelShown,
          'is-clickable': isClickable(item),
        },
      ]"
      :style="{ transform: `translateX(${item.x}px)` }"
      :title="isClickable(item) ? 'Вернуться к этапу' : ''"
      @click="select(item)"
    >
      <div class="step__icon">
        <i v-if="item.status === 'success'" class="el-icon-check"></i>
        <span v-else>{{ item.number }}</span>
      </div>
      <div class="step__label">
        <div class="step__title">{{ item.step.title }}</div>
        <div v-if="item.step.description" class="step__description">
          {{ item.step.description }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const RADIUS = 12;
const GAP = 6;
// Расстояние между центрами соседних кружков, при котором линия между ними нулевой длины
const ADJACENT = 2 * (RADIUS + GAP);

export default {
  name: "stepLine",
  props: {
    // [{ name, title, description? }]
    steps: { type: Array, required: true },
    active: { type: Number, default: 0 },
    // Пройденные этапы можно нажать - событие select с индексом этапа
    clickable: { type: Boolean, default: false },
  },
  data() {
    return {
      ENTER: 250,
      MOVE: 450,
      width: 0,
      // Отрисованное состояние: { key, step, number, x, shown, labelShown }
      items: [],
      runId: 0,
      noTransition: false,
      resizeObserver: null,
    };
  },

  computed: {
    indexByKey() {
      const map = {};
      this.steps.forEach((step, i) => (map[step.name] = i));
      return map;
    },
    hasDescription() {
      return this.steps.some((step) => step.description);
    },
    views() {
      return this.items.map((item) => {
        const index = this.indexByKey[item.key];
        let status = "wait";
        if (index < this.active) status = "success";
        else if (index === this.active) status = "process";
        return { ...item, status };
      });
    },
    lines() {
      const lines = [];
      for (let i = 1; i < this.views.length; i++) {
        const a = this.views[i - 1];
        const b = this.views[i];
        lines.push({
          // Линия принадлежит кружку справа, чтобы при вставке/удалении в середине она плавно переезжала
          key: b.key,
          left: a.x + RADIUS + GAP,
          width: Math.max(0, b.x - a.x - ADJACENT),
          done: a.status === "success",
        });
      }
      return lines;
    },
  },

  watch: {
    steps() {
      this.sync();
    },
  },

  methods: {
    isClickable(item) {
      return this.clickable && item.status === "success";
    },

    select(item) {
      if (this.isClickable(item)) this.$emit("select", this.indexByKey[item.key]);
    },

    layout(count) {
      return Array.from(
        { length: count },
        (_, i) => ((i + 0.5) * this.width) / count
      );
    },

    newItem(step) {
      return {
        key: step.name,
        step,
        number: 0,
        x: 0,
        shown: false,
        labelShown: false,
      };
    },

    refreshSteps(items) {
      items.forEach((item) => {
        const index = this.indexByKey[item.key];
        if (index === undefined) return;
        item.step = this.steps[index];
        item.number = index + 1;
      });
    },

    // Ставит неопорные элементы вплотную к ближайшему опорному слева (или справа, если слева нет)
    collapse(list, isAnchor) {
      let prevX = null;
      let offset = 0;
      let pending = [];
      list.forEach((item) => {
        if (isAnchor(item)) {
          pending.forEach(
            (p, k) => (p.x = item.x - ADJACENT * (pending.length - k))
          );
          pending = [];
          prevX = item.x;
          offset = 0;
        } else if (prevX !== null) {
          item.x = prevX + ADJACENT * ++offset;
        } else {
          pending.push(item);
        }
      });
      if (pending.length) {
        const target = this.layout(pending.length);
        pending.forEach((p, k) => (p.x = target[k]));
      }
    },

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    nextFrame() {
      return new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );
    },

    // Мгновенно выставить итоговое состояние без анимации
    async snap() {
      this.runId++;
      const existing = new Map(this.items.map((item) => [item.key, item]));
      const target = this.layout(this.steps.length);
      this.items = this.steps.map((step, i) =>
        Object.assign(existing.get(step.name) || this.newItem(step), {
          x: target[i],
          shown: true,
          labelShown: true,
        })
      );
      this.refreshSteps(this.items);
      this.noTransition = true;
      await this.nextFrame();
      this.noTransition = false;
    },

    async sync() {
      if (!this.width) return this.snap();
      const run = ++this.runId;
      const alive = () => run === this.runId;
      const inSteps = (item) => item.key in this.indexByKey;
      this.refreshSteps(this.items);

      // Удаление: оставшиеся разъезжаются по местам, удаляемые прижимаются к соседу и гаснут
      const removed = this.items.filter((item) => !inSteps(item));
      if (removed.length) {
        const kept = this.items.filter(inSteps);
        const target = this.layout(kept.length);
        kept.forEach((item, i) => (item.x = target[i]));
        this.collapse(this.items, inSteps);
        removed.forEach((item) => (item.labelShown = false));
        await this.wait(this.MOVE);
        if (!alive()) return;
        removed.forEach((item) => (item.shown = false));
        await this.wait(this.ENTER);
        if (!alive()) return;
        this.items = kept;
      }

      // Добавление: новый кружок появляется вплотную за соседом, линия пока нулевой длины
      const existing = new Map(this.items.map((item) => [item.key, item]));
      const next = this.steps.map(
        (step) => existing.get(step.name) || this.newItem(step)
      );
      const added = next.filter((item) => !existing.has(item.key));
      this.refreshSteps(next);
      if (added.length) {
        this.collapse(next, (item) => existing.has(item.key));
        this.items = next;
        await this.nextFrame();
        if (!alive()) return;
        added.forEach((item) => (item.shown = true));
        await this.wait(this.ENTER);
        if (!alive()) return;
      } else {
        this.items = next;
      }

      // Перемещение всех этапов на новые места
      const target = this.layout(next.length);
      next.forEach((item, i) => {
        item.x = target[i];
        item.shown = true;
      });
      if (added.length) {
        await this.wait(this.MOVE);
        if (!alive()) return;
      }
      next.forEach((item) => (item.labelShown = true));
    },

    onResize() {
      const width = this.$refs.root.clientWidth;
      if (width === this.width) return;
      this.width = width;
      this.snap();
    },
  },

  mounted() {
    this.width = this.$refs.root.clientWidth;
    this.snap();
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs.root);
  },

  beforeDestroy() {
    this.runId++;
    this.resizeObserver.disconnect();
  },
};
</script>

<style scoped>
.step-line {
  position: relative;
  height: 56px;
  --wait: #c0c4cc;
  --process: #303133;
  --success: #67c23a;
}
.step-line.has-description {
  height: 76px;
}
.step-line.no-transition,
.step-line.no-transition * {
  transition: none !important;
}

.step-line__line {
  position: absolute;
  top: 11px;
  left: 0;
  height: 2px;
  background: var(--wait);
  transition: transform var(--move) ease-in-out, width var(--move) ease-in-out,
    background-color 0.3s;
}
.step-line__line.is-done {
  background: var(--success);
}

.step {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  transition: transform var(--move) ease-in-out;
}
.step__icon {
  position: absolute;
  top: 0;
  left: -12px;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color);
  border-radius: 50%;
  background: #fff;
  color: var(--color);
  font-size: 14px;
  opacity: 0;
  transform: scale(0);
  transition: transform var(--enter) ease-out, opacity var(--enter),
    color 0.3s, border-color 0.3s;
}
.step.is-shown .step__icon {
  opacity: 1;
  transform: scale(1);
}
.step__label {
  position: absolute;
  top: 30px;
  left: -70px;
  width: 140px;
  text-align: center;
  color: var(--color);
  opacity: 0;
  transition: opacity var(--enter), color 0.3s;
}
.step.is-label-shown .step__label {
  opacity: 1;
}
/* Как заголовки групп карточек («Поступление», «Расход»), цвет - по статусу этапа */
.step__title {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.step__description {
  font-size: 12px;
  line-height: 18px;
}

.step.is-clickable .step__icon,
.step.is-clickable .step__label {
  cursor: pointer;
}
.step.is-clickable:hover {
  --color: #409eff;
}

.step.is-wait {
  --color: var(--wait);
}
.step.is-process {
  --color: var(--process);
}
.step.is-success {
  --color: var(--success);
}
</style>
